import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGES, BBN_SECURITY } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import {
  ArrowRight,
  Phone,
  ShieldCheck,
  Home,
  Building2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Sparkles,
  Hammer,
  Shield,
} from "lucide-react";
import {
  GuideBody,
  GuideFaq,
  GuideRelated,
  CheckList,
  buildGuideSchemas,
  type GuideMeta,
} from "@/components/GuideLayout";
import { Reveal } from "@/components/Reveal";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

const meta: GuideMeta = {
  category: "Guide · Säkerhet",
  title: "Splitterskydd för fönster — säkerhetsfilm för hem & företag",
  tldr:
    "Splitterskydd på fönster håller ihop glasrutan om den krossas vid inbrott, olycka eller explosion — istället för att splittras i vassa skärvor. Det vanligaste och mest kostnadseffektiva alternativet är säkerhetsfilm (100–375 µm polyesterfilm) som monteras på befintligt glas och uppfyller EN 12600-klassning. Säkerhetsfilm kostar 70–85 % mindre än att byta till laminerat glas, är klar och syns inte, och ger inbrottsfördröjning på 30–90 sekunder — tillräckligt för att avskräcka 90 % av tillfällesinbrotten.",
  readTimeMin: 9,
  path: "/splitterskydd-fonster",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Vad är skillnaden mellan säkerhetsfilm och splitterskydd?",
      a: "Det är samma produkt. \"Splitterskyddsfilm\" är det svenska arbetsmiljö-uttrycket (AFS 2020:1 — skydd mot fallande glas), \"säkerhetsfilm\" är försäkringsbolags- och installatörsuttrycket. Båda syftar på en tjock polyesterfilm (100–375 µm) som håller ihop krossade glasbitar i ramen istället för att låta dem falla ner.",
    },
    {
      q: "Stoppar säkerhetsfilm en inbrottstjuv?",
      a: "Den stoppar inte glaset från att gå sönder — men den håller ihop skärvorna och förhindrar att tjuven får upp en öppning på 30–90 sekunder. För 90 % av tillfällesinbrott är detta tillräckligt eftersom tjuven hör larm gå och flyttar vidare. För högvärdesskydd kombineras film med larm och förstärkta gångjärn.",
    },
    {
      q: "Är säkerhetsfilm lika bra som laminerat glas?",
      a: "Funktionellt likvärdigt: båda håller ihop glaset. Skillnaden är att laminerat glas är inbyggt i glaset från fabrik (PVB-folie mellan två glasskivor) medan film monteras på befintligt glas. För nybygge är laminerat glas en ren konstruktionsdetalj. För befintliga fastigheter är säkerhetsfilm 70–85 % billigare och kan monteras på en arbetsdag utan glasbyte.",
    },
    {
      q: "Vilken tjocklek på säkerhetsfilm behöver jag?",
      a: "100 µm (4 mil): basal splitterskydd för inomhusmiljöer och AFS 2020:1-krav. 175 µm (7 mil): standardval för butiksfönster och privata villor mot inbrott. 300–375 µm (12–15 mil): högsta nivån — bank, juvelerare, glasdörrar i offentliga miljöer. Vi rekommenderar 175 µm för 80 % av alla privata installationer.",
    },
    {
      q: "Måste säkerhetsfilm kombineras med ramfäste (attachment system)?",
      a: "För certifierat splitterskydd enligt EN 12600 ska filmen \"löpa till\" minst 6 mm in i karmen. För ren glassplitter-funktion (utan klassning) räcker det att filmen monteras på hela glasytan. Vid arbetsplatskrav (AFS 2020:1) eller hög inbrottsnivå monteras filmen tillsammans med ett strukturellt fäste (Dow 995 eller motsvarande) som limmar filmen mot karmen — då uppnås också EN 12600 Class B/C-klassning.",
    },
    {
      q: "Påverkar säkerhetsfilm fönstrets utseende eller ljus?",
      a: "Klar säkerhetsfilm är optiskt nästan neutral — den syns endast som en svag reflektion i vissa vinklar. Ljustransmissionen är 85–88 % (vanligt glas är ~90 %). Filmen kan kombineras med solfilm i samma installation för dubbel funktion: värmereduktion + splitterskydd.",
    },
    {
      q: "Sänker säkerhetsfilm hemförsäkringen?",
      a: "I många fall — kontakta ditt försäkringsbolag och be om \"glaskrossförsäkring med säkerhetsfilm-rabatt\". Folksam, Länsförsäkringar och If brukar erbjuda 5–15 % premiesänkning på fastighetsförsäkringen vid dokumenterad installation av certifierad säkerhetsfilm.",
    },
  ],
  related: [
    { to: "/sakerhetsfilm", label: "Säkerhetsfilm — produktöversikt" },
    { to: "/sakerhetsfilm/solar-screen", label: "Solar Screen säkerhetsfilm" },
    { to: "/guider/sakerhetsfilm-inbrottsskydd", label: "Inbrottsskydd & EN 12600 — komplett guide" },
    { to: "/auktoriserad-installator", label: "Hitta certifierad montör" },
  ],
};

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Så väljer du rätt splitterskydd för dina fönster",
  totalTime: "PT10M",
  step: [
    { "@type": "HowToStep", position: 1, name: "Identifiera behovet", text: "Privat: inbrottsskydd mot tillfälle och vandalisering. Företag: arbetsmiljökrav (AFS 2020:1), butiksinbrott, kassaytor." },
    { "@type": "HowToStep", position: 2, name: "Välj filmtjocklek", text: "100 µm för basskydd, 175 µm för standard inbrotts- och splitterskydd, 300–375 µm för bank/juvelerare/offentliga miljöer." },
    { "@type": "HowToStep", position: 3, name: "Bedöm behov av ramfäste", text: "Krävs vid certifiering enligt EN 12600 eller arbetsmiljöbeslut. Inte krävt för ren splitterskydd-funktion." },
    { "@type": "HowToStep", position: 4, name: "Anlita auktoriserad installatör", text: "Auktoriserad 3M-installatör eller motsvarande — annars gäller inte produktgarantin." },
    { "@type": "HowToStep", position: 5, name: "Kombinera vid behov", text: "Säkerhetsfilm kan monteras tillsammans med solfilm i en kombinationsfilm för dubbel funktion utan extra installation." },
  ],
};

export const Route = createFileRoute("/splitterskydd-fonster")({
  head: () => ({
    meta: [
      { title: "Splitterskydd för fönster — säkerhetsfilm hem & företag | BBN Nordic" },
      {
        name: "description",
        content:
          "Säkerhetsfilm håller glaset på plats vid inbrott och olyckor — 70–85 % billigare än laminerat glas. Komplett guide för hem & företag: tjocklekar, EN 12600, AFS 2020:1 och jämförelse.",
      },
      { property: "og:title", content: "Splitterskydd för fönster — säkerhetsfilm för hem & företag" },
      { property: "og:description", content: "Säkerhetsfilm jämfört med laminerat glas, tjocklekar, AFS 2020:1 och kostnad. Officiella auktoriserade 3M-installatörer i Sverige." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://bbnnordic.se/splitterskydd-fonster" },
      { property: "og:image", content: IMAGES.sakerhetsfilmHero },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/splitterskydd-fonster" }],
    scripts: [
      ...buildGuideSchemas(meta),
      { type: "application/ld+json", children: JSON.stringify(howToSchema) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      {/* Hero med direkt-svar */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy overflow-hidden">
        <HeroBgImage src={BBN_SECURITY[5]} alt="Splitterskyddsfilm på fönster — 3M säkerhetsfilm" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
              <ShieldCheck size={14} />
              <span>Guide · Säkerhet & splitterskydd</span>
              <span className="text-cream/70">·</span>
              <span className="text-cream/80">{meta.readTimeMin} min läsning</span>
            </div>
            <h1 className="font-display text-cream text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Splitterskydd för fönster — <em className="italic text-gold">säkerhetsfilm</em> för hem & företag
            </h1>

            <aside className="mt-8 rounded-2xl border border-gold/30 bg-gold/10 backdrop-blur-sm p-5 md:p-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold-bright font-semibold mb-3">
                <Sparkles size={12} /> Kort svar
              </div>
              <p className="text-cream/95 text-base md:text-lg leading-relaxed">
                <strong className="text-gold-bright">Säkerhetsfilm</strong> (även kallat splitterskyddsfilm eller skyddsfilm)
                är en tjock polyesterfilm på 100–375 µm som monteras på befintligt fönsterglas
                och håller ihop glaset om det krossas vid inbrott eller olycka. För befintliga
                fastigheter är säkerhetsfilm <strong className="text-gold-bright">70–85 % billigare</strong> än
                att byta till laminerat glas, ger 30–90 sekunders inbrottsfördröjning, uppfyller
                EN 12600 och AFS 2020:1 och kan monteras på en arbetsdag utan att glasrutorna
                behöver demonteras.
              </p>
            </aside>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
              <Link
                to="/offert"
                search={{ produkt: "Säkerhetsfilm" }}
                className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
              >
                Få offert på säkerhetsfilm <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+46771502502"
                className="inline-flex items-center justify-center gap-2 text-cream/85 px-4 py-3 font-medium hover:text-gold-bright transition-colors min-h-11"
              >
                <Phone size={16} /> Ring 0771 502 502
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <PostHeroConversion />

      {/* Privat vs företag — split block */}
      <section className="py-16 md:py-20 bg-card/30 border-y border-border">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">För vem</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground">
              Splitterskydd för hem och arbetsplatser.
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl font-light">
              Hemma är inbrottsfördröjning ofta viktigast. På arbetsplatser tillkommer arbetsmiljökrav. Vi använder samma splitterskyddsfilm och monteringssätt för båda behoven.
            </p>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Reveal>
              <article className="h-full rounded-3xl border border-gold/20 bg-card/60 p-7 md:p-9 hover:border-gold/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-gold/15 p-2.5"><Home size={22} className="text-gold" /></div>
                  <h3 className="text-2xl font-display text-foreground">Privatpersoner — hem & villa</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  I hemmet används filmen främst för att fördröja inbrott genom fönster i markplan, terrassdörrar, källarfönster och glas i garagedörrar. Den skyddar också mot vandalism i utsatta lägen och håller kvar fallande glas vid lek eller arbete hemma.
                </p>
                <ul className="mt-5 space-y-2.5 text-sm">
                  {[
                    "Terrassdörrar & markplan-fönster — 90 % av villor­inbrott sker här",
                    "Barnkammare & lekrum — splitterskydd om bollen träffar glaset",
                    "Glasdörrar & innerväggsglas — AFS-krav även privat (≥1 800 mm)",
                    "Bostadsrätt — kräver inget styrelsegodkännande (invändigt)",
                    "5–15 % rabatt på hemförsäkringens glasdel hos flera bolag",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-foreground/80">
                      <CheckCircle2 size={16} className="text-gold mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/offert"
                  search={{ produkt: "Säkerhetsfilm" }}
                  className="mt-6 inline-flex items-center gap-2 text-gold-bright font-semibold hover:text-gold transition-colors"
                >
                  Begär offert för hem <ArrowRight size={16} />
                </Link>
              </article>
            </Reveal>

            <Reveal delay={0.1}>
              <article className="h-full rounded-3xl border border-gold/20 bg-card/60 p-7 md:p-9 hover:border-gold/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-gold/15 p-2.5"><Building2 size={22} className="text-gold" /></div>
                  <h3 className="text-2xl font-display text-foreground">Företag — butik, kontor & fastighet</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  För företag gäller det ofta arbetsmiljökrav enligt AFS 2020:1 och skydd mot butiksinbrott och smash-and-grab. Vi monterar också film vid kassaytor och i offentliga miljöer där glaskross kan orsaka allvarliga olyckor.
                </p>
                <ul className="mt-5 space-y-2.5 text-sm">
                  {[
                    "Butiks­fönster mot gata — 175–300 µm rekommenderas",
                    "Arbetsplatser med glas <1 800 mm AFS 2020:1 — obligatoriskt",
                    "Kassaytor, apotek, bensinstationer — splitterskydd + larm",
                    "Skolor, vårdcentraler & offentliga miljöer — barn- och patientskydd",
                    "Reception & entréer — kombineras ofta med dekorfilm för avskärmning",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-foreground/80">
                      <CheckCircle2 size={16} className="text-gold mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/offert"
                  search={{ produkt: "Säkerhetsfilm" }}
                  className="mt-6 inline-flex items-center gap-2 text-gold-bright font-semibold hover:text-gold transition-colors"
                >
                  Begär offert för företag <ArrowRight size={16} />
                </Link>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <GuideBody>
        <h2>Hur fungerar splitterskydd på fönster?</h2>
        <p>
          När en glasruta krossas av en inbrottstjuv, en boll, vind eller en explosion
          går det vanliga glaset i hundratals vassa skärvor som faller ner. Säkerhetsfilm är
          en optiskt klar polyesterfilm i 100–375 µm tjocklek, monterad på glasets insida
          med ett strukturellt lim. När glaset krossas <strong>håller filmen ihop skärvorna i
          en sammanhängande matta</strong> som sitter kvar i karmen istället för att rasa ner.
        </p>
        <p>För inbrottstjuven betyder det att glaset går sönder, men öppningen
        skapas inte. Det krävs 30–90 sekunders bearbetning med kofot eller hammare för att
        bryta igenom filmen. 90 % av tillfällesinbrotten avbryts inom 60 sekunder.</p>

        <h2>Olika splitterskyddslösningar — jämförelse</h2>
        <p>
          Här jämför vi alternativen för splitterskydd på fönster och visar när de passar:
        </p>
        <div className="not-prose overflow-x-auto rounded-2xl border border-gold/15 my-6">
          <table className="w-full text-sm">
            <caption className="sr-only">Jämförelse mellan splitterskyddslösningar för fönster.</caption>
            <thead>
              <tr className="bg-navy-light/40 border-b border-gold/15">
                <th scope="col" className="text-left p-4 font-semibold text-foreground">Lösning</th>
                <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Kostnad / m²</th>
                <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Splitterskydd</th>
                <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Inbrottsförd.</th>
                <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Installation</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Säkerhetsfilm 100 µm", "650–950 kr", "Bas", "20–30 sek", "Befintligt glas, 1 dag"],
                ["Säkerhetsfilm 175 µm", "950–1 400 kr", "Standard", "45–60 sek", "Befintligt glas, 1 dag"],
                ["Säkerhetsfilm 300 µm + fäste", "1 600–2 200 kr", "Hög (EN 12600 B/C)", "60–90 sek", "Befintligt glas, 1–2 dagar"],
                ["Laminerat glas (nytt)", "4 500–7 500 kr", "Hög", "60–120 sek", "Glasbyte krävs, vecka"],
                ["Härdat glas (nytt)", "2 800–4 200 kr", "Lågt — splittrar i småbitar", "0–10 sek", "Glasbyte krävs"],
                ["Trådglas (gammalt)", "—", "Medel", "20–40 sek", "Saluförs ej för nybygge"],
              ].map(([sol, price, split, delay, install], i) => (
                <tr key={sol} className={i % 2 ? "bg-navy-light/10" : ""}>
                  <th scope="row" className={`text-left p-4 font-medium ${sol === "Säkerhetsfilm 175 µm" ? "text-gold-bright" : "text-foreground"}`}>{sol}</th>
                  <td className="p-4 text-muted-foreground">{price}</td>
                  <td className="p-4 text-muted-foreground">{split}</td>
                  <td className="p-4 text-muted-foreground">{delay}</td>
                  <td className="p-4 text-muted-foreground">{install}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground italic">
          Priserna inkluderar material och montering i Skåne, 2026. Säkerhetsfilm 175 µm är vår mest sålda produkt och täcker 80 % av både privata och kommersiella behov.
        </p>

        <h2>Säkerhetsfilm vs laminerat glas — direkt jämförelse</h2>
        <p>
          Frågan vi får oftast: <em>"Är säkerhetsfilm lika bra som laminerat glas?"</em> Funktionellt är de
          mycket lika. Skillnaden ligger i kostnad, installation och designfrihet:
        </p>
        <div className="not-prose overflow-x-auto rounded-2xl border border-gold/15 my-6">
          <table className="w-full text-sm">
            <caption className="sr-only">Säkerhetsfilm vs laminerat glas — direkt jämförelse.</caption>
            <thead>
              <tr className="bg-navy-light/40 border-b border-gold/15">
                <th scope="col" className="text-left p-4 font-semibold text-foreground">Egenskap</th>
                <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Laminerat glas</th>
                <th scope="col" className="text-left p-4 font-semibold text-gold-bright">
                  <span className="inline-flex items-center gap-1.5"><Sparkles size={14} /> Säkerhetsfilm</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Splitterskydds-funktion", "Ja (PVB-folie)", "Ja (polyesterfilm)"],
                ["EN 12600-klassning möjlig", "Ja, klass B/C", "Ja, klass B/C med fäste"],
                ["Inbrottsfördröjning", "60–120 sek", "30–90 sek beroende på tjocklek"],
                ["Kostnad per m² (inkl. installation)", "4 500–7 500 kr", "650–2 200 kr (70–85 % lägre)"],
                ["Installation på befintligt glas", "Nej — kräver glasbyte", "Ja — på en arbetsdag"],
                ["Ledtid", "2–6 veckor (måttbeställs)", "1–2 veckor"],
                ["Påverkar fasaden / bygglov", "Vid stora glasbyten ibland", "Aldrig"],
                ["Kombinerbar med solfilm", "Sällan — köps som separat produkt", "Ja, i samma installation"],
                ["UV-skydd inkluderat", "99 % (PVB blockerar)", "99 % (de flesta filmer)"],
                ["Optisk klarhet", "Identisk med vanligt glas", "85–88 % ljustransmission"],
                ["Livslängd", "20–30 år (glasets livslängd)", "15–20 år"],
                ["Bäst för", "Nybygge, fönsterbyten, högsäkerhet", "Befintliga fastigheter, retrofit"],
              ].map(([f, l, s], i) => (
                <tr key={f} className={i % 2 ? "bg-navy-light/10" : ""}>
                  <th scope="row" className="text-left p-4 text-foreground font-medium">{f}</th>
                  <td className="p-4 text-muted-foreground">{l}</td>
                  <td className="p-4 text-gold-bright font-medium">{s}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          <strong>Slutsats:</strong> Vid nybygge och fönsterbyte → laminerat glas är rätt val.
          Vid befintliga fastigheter där glaset redan sitter på plats → säkerhetsfilm är
          70–85 % billigare och nästan likvärdigt funktionellt.
        </p>

        <h2>Hur säkerhetsfilm förhindrar glassplitter vid olyckor</h2>
        <p>
          Glaskross­skador i Sverige står för ca <strong>3 800 skadeanmälningar per år</strong> till
          Arbetsmiljöverket, främst i butiker, kontor, restauranger och offentliga miljöer.
          Den vanligaste skadetypen är skärsår från fallande glasskärvor när någon springer
          in i ett glas (typ: glasdörr som tas för en öppning) eller när ett glas krossas av
          en boll, ett möbel­tipp eller en explosion.
        </p>
        <p>
          Så håller säkerhetsfilm ihop glaset:
        </p>
        <ul>
          <li><strong>Adhesion (vidhäftning).</strong> Filmen limmas mot glasytan med ett akrylat-baserat strukturlim som tål 50+ kg/cm² draglast.</li>
          <li><strong>Hög draghållfasthet.</strong> Polyestermaterialet (PET) har en draghållfasthet på 200+ N/mm² — högre än konstruktionsstål per vikt.</li>
          <li><strong>Mekanisk inneslutning.</strong> När glaset krossas omsluter filmen varje enskild skärva i ett sammanhängande nät.</li>
        </ul>
        <p>
          Resultatet är att <strong>inga skärvor faller fritt</strong> även om hela rutan
          krossas. De förblir i ramens position. Det är grundprincipen bakom AFS 2020:1 §16
          som ställer krav på splitterskydd för glas på arbetsplatser under 1 800 mm.
        </p>

        <h2>Vilka standarder ska splitterskyddet uppfylla?</h2>
        <CheckList
          items={[
            "EN 12600 — Klass A (lägst) till C (högst). Vid certifiering enligt EN 12600 B/C krävs strukturellt ramfäste utöver filmen.",
            "AFS 2020:1 §16 — Arbetsmiljöverkets föreskrift om glas på arbetsplatser. Glasytor under 1 800 mm där människor rör sig ska ha splitterskydd.",
            "EN 356 P1A–P5A — Inbrottsklassning. P2A (drop-ball ≥6 kg) uppnås med 175 µm säkerhetsfilm.",
            "ISO 16933 — Explosionsskydd. Relevant för ambassader, banker, polishus och utsatta offentliga byggnader.",
            "BFS 2011:6 — Boverkets allmänna råd om bostäder, kapitel 3:411 om personskydd vid glas.",
          ]}
        />

        <h2>Vad kostar splitterskydd för fönster?</h2>
        <p>
          Här är typiska prisspann för installation i Skåne 2026 — inklusive material, arbete
          och 10 års produktgaranti:
        </p>
        <ul>
          <li><strong>Villa, terrassdörrar + 4 markplansfönster (≈12 m²):</strong> 12 000–18 000 kr</li>
          <li><strong>Butiksfönster mot gata (≈20 m²):</strong> 22 000–32 000 kr</li>
          <li><strong>Kontorsfastighet, hela bottenvåningen (≈80 m²):</strong> 75 000–115 000 kr</li>
          <li><strong>Skolglas / vårdmiljö (≈150 m²):</strong> 130 000–200 000 kr</li>
        </ul>
        <p>
          Jämför med laminerat glas där samma villa hade kostat 55 000–90 000 kr i glasbyte
          (exklusive följdarbeten på karm och list).
        </p>

        <h2>Alternativ när film eller laminerat glas inte räcker</h2>
        <p>
          Om varken säkerhetsfilm eller laminerat glas ger det skydd du behöver finns de här alternativen:
        </p>
        <ul>
          <li><strong>Polykarbonatglas (Lexan).</strong> 200× hårdare än vanligt glas, oförstörbar med kofot. Används i banker och fängelsemiljöer. Pris: 5 000–9 000 kr/m².</li>
          <li><strong>Härdat säkerhetsglas + film.</strong> Härdat glas splittrar i ofarliga småbitar (krav i bilrutor) men ger inget inbrottsskydd — kombineras därför ibland med säkerhetsfilm.</li>
          <li><strong>Galler eller rullgaller.</strong> Visuellt påträngande men 100 % inbrottsstoppande. Används vid juvelerare och vapen­butiker.</li>
        </ul>
        <p>
          För 95 % av alla bostäder, kontor och butiker är säkerhetsfilm rätt avvägning
          mellan funktion, kostnad och estetik.
        </p>
      </GuideBody>

      {/* Stor CTA — prefyllt formulär */}
      <section className="bg-navy py-16 md:py-20 border-y border-gold/20">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold-bright font-semibold mb-5">
            <Shield size={14} /> Auktoriserad 3M-installatör · 10 års garanti
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-cream mb-5">
            Få offert på säkerhetsfilm — fast pris
          </h2>
          <p className="text-cream/80 max-w-xl mx-auto mb-8 leading-relaxed">
            Vi kommer ut, mäter glasytorna och rekommenderar rätt filmtjocklek utifrån behovet. Formuläret nedan är förvalt för <strong className="text-gold-bright">Säkerhetsfilm</strong> så du kommer rätt direkt.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/offert"
              search={{ produkt: "Säkerhetsfilm" }}
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded-full font-semibold min-h-12"
            >
              Begär offert (Säkerhetsfilm förvald) <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-6 py-4 rounded-full font-semibold hover:bg-gold/10 transition min-h-12"
            >
              <Phone size={16} /> 0771-502 502
            </a>
          </div>
          <p className="mt-6 text-xs text-cream/80">
            3 500+ nöjda kunder · AAA-kreditvärdighet · EN 12600 & AFS 2020:1
          </p>
        </div>
      </section>

      <GuideFaq items={meta.faqs ?? []} />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
